import React, { useState, useEffect } from 'react';
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Form, Button, Row, Col, DatePicker, Input, Divider, Skeleton, message, Table, Card, Tabs, } from 'antd';
import AnioPeriodo from '../../../../components/forms/anio_periodo';
import { getAplicacionAguinaldo, createMovimientoDiasHoras, CancelarMovimientoDiasHoras, getEmpleadosActivos, getMovimientosDiasHoras } from '../../../../apis/AplicacionAguinaldoApi';
import { getEmpleadosSinAguinaldo } from '../../../../apis/CalculoAguinaldoApi';
import { status_period } from '@app/constants/period';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const routes = [
  {
    href: '',
    title: (
      <>
        <HomeOutlined />
        <span>Inicio</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Nomina</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <span>Cálculos Anuales</span>
      </>
    ),
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Aplicación de Aguinaldo</span>
      </>
    ),
  },
];

const columnsConAguinaldo = [
  { 
    title: 'ID', 
    dataIndex: 'empleado', 
    width: 90,
    sorter: (a, b) => Number(a.empleado || 0) - Number(b.empleado || 0),
    sortDirections: ['ascend', 'descend'],
  },
  { title: 'Nombre', 
    dataIndex: 'nombre',
    sorter: (a, b) => (a.nombre || '').localeCompare(b.nombre || ''),
    sortDirections: ['ascend', 'descend'],
  },
  {
    title: 'Fecha Ingreso',
    dataIndex: 'fecha_ingreso',
    render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : ''),
  },
  {
    title: 'Días a Considerar',
    dataIndex: 'dias_considerar',
    align: 'center',
  },
  {
    title: 'Faltas',
    dataIndex: 'faltas',
    align: 'center',
  },
  {
    title: 'Incapacidades',
    dataIndex: 'incapacidades',
    align: 'center',
  },
  {
    title: 'Días Aguinaldo',
    dataIndex: 'aguinaldo_calculado',
    align: 'right',
  },
];

const columnsSinAguinaldo = [
  { title: 'Empleado', dataIndex: 'empleado', width: 90 },
  { title: 'Nombre', dataIndex: 'nombre' },
  {
    title: 'Fecha Ingreso',
    dataIndex: 'fecha_ingreso',
    render: (value) => (value ? dayjs(value).format('DD/MM/YYYY') : ''),
  },
];

const AplicacionAguinaldo = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [objectSelected, setObjectSelected] = useState(null);
  const [disabledForm, setDisabled] = useState(true);
  const [header, setHeader] = useState(null);
  const [period, setPeriod] = useState(null);
  const [rowsConAguinaldo, setRowsConAguinaldo] = useState([]);
  const [rowsSinAguinaldo, setRowsSinAguinaldo] = useState([]);
  const [estatusPeriodo, setEstatusPeriodo] = useState(null);
  const [todosAplicados, setTodosAplicados] = useState(false);
  const [ningunMovimiento, setNingunMovimiento] = useState(true);


  const anioActual = new Date().getFullYear();

  const refreshButtonStates = async () => {
    if (!period || !period.periodoCompleto) {
      setTodosAplicados(false);
      setNingunMovimiento(true);
      return;
    }

    const { periodo_id, num_periodo, anio } = period.periodoCompleto;

    try {
      const [resActivos, resMovimientos] = await Promise.all([
        getEmpleadosActivos(),
        getMovimientosDiasHoras(periodo_id, anio, num_periodo),
      ]);

      if (resActivos?.ok && resMovimientos?.ok) {
        const idsActivos = (resActivos.payload || []).map((e) => Number(e.id)).filter((id) => !isNaN(id));
        const idsConMovimiento = (resMovimientos.payload || []).map((m) => Number(m.id_empleado)).filter((id) => !isNaN(id));
        
        const setActivos = new Set(idsActivos);
        const setMovimientos = new Set(idsConMovimiento);

        // Todos aplicados: todos los activos están en movimientos
        const todosEstanAplicados = idsActivos.length > 0 && idsActivos.every((id) => setMovimientos.has(id));
        setTodosAplicados(todosEstanAplicados);

        // Ningún movimiento: no hay ningún movimiento registrado
        const noHayMovimientos = idsConMovimiento.length === 0;
        setNingunMovimiento(noHayMovimientos);
      } else {
        setTodosAplicados(false);
        setNingunMovimiento(true);
      }
    } catch (err) {
      console.error('Error refrescando estados de botones:', err);
      setTodosAplicados(false);
      setNingunMovimiento(true);
    }
  };

  const onBlurHeader = async (values, periodData) => {
    setHeader(values);

    if (values?.anio && values?.num_periodo && values?.periodo_id) {
      setDisabled(false);
    } else {
      setDisabled(true);
    }

    if (periodData) {
      setPeriod(periodData);
    } else {
      setPeriod(null);
    }
  };

  const handleMostrar = async () => {
    try {
      const values = await form.validateFields();
      const anioAguinaldo = values.anio_aguinaldo;

      if (!anioAguinaldo) {
        message.warning('Debes indicar el Año Aguinaldo.');
        return;
      }

      setLoading(true);
      setRowsConAguinaldo([]);
      setRowsSinAguinaldo([]);

      // Empleados con cálculo de aguinaldo
      const resCon = await getAplicacionAguinaldo(anioAguinaldo);

      if (!resCon || !resCon.ok) {
        message.error(resCon?.message || 'Error al obtener la aplicación de aguinaldo.');
      } else {
        const listaCon = resCon.payload || [];
        setRowsConAguinaldo(
          listaCon.map((item) => ({
            key: item.empleado_id ?? item.id,
            empleado: item.empleado_id,
            nombre: item.nombre,
            fecha_ingreso: item.fecha_ingreso,
            dias_considerar: item.dias_considerar,
            dias_aguinaldo: item.dias_aguinaldo,
            faltas: item.faltas,
            incapacidades: item.incapacidades,
            aguinaldo_calculado: item.aguinaldo_calculado,
          }))
        );
      }

      // Empleados SIN aguinaldo
      const resSin = await getEmpleadosSinAguinaldo(anioAguinaldo);

      if (!resSin || !resSin.ok) {
        message.error(resSin?.message || 'Error al obtener empleados sin aguinaldo.');
      } else {
        const listaSin = resSin.payload || [];
        setRowsSinAguinaldo(
          listaSin.map((item) => ({
            key: item.id,
            empleado: item.id,
            nombre: item.nombre,
            fecha_ingreso: item.fecha_ingreso,
          }))
        );
      }

      // Refrescar estados de botones
      await refreshButtonStates();
    } catch (err) {
      // Nada a consola, solo mensaje amigable
      message.error('Ocurrió un error al mostrar la información del aguinaldo.');
    } finally {
      setLoading(false);
    }
  };


  const handleAplicar = async () => {
    try {
      const values = await form.validateFields();

      // Validaciones mínimas
      if (!period || !period.periodoCompleto) {
        message.warning('Selecciona un período válido.');
        return;
      }

      if (estatusPeriodo > status_period.calculado_total) {
        message.warning('El período seleccionado ya está cerrado o timbrado. No se puede aplicar el aguinaldo.');
        return;
      }

      const { periodo_id, num_periodo } = period.periodoCompleto;
      const anio = values.anio_aguinaldo;
      if (!anio) {
        message.warning('Falta el año de nómina (anio).');
        return;
      }

      // Filtrar solo registros con aguinaldo calculado
      const registros = rowsConAguinaldo.filter(
        (row) => row.aguinaldo_calculado !== undefined && row.aguinaldo_calculado !== null
      );
      if (!registros.length) {
        message.warning('No hay cálculos de aguinaldo para aplicar.');
        return;
      }

      setLoading(true);

      let errores = 0;
      for (const row of registros) {
        const diasNumber = Number(row.aguinaldo_calculado);
        if (!diasNumber) {
          errores += 1;
          message.warning(`Aguinaldo calculado inválido para ${row.nombre || row.empleado}.`);
          continue;
        }

        const payload = {
          periodo_id,
          anio,
          num_periodo,
          odoo_empleado_id: row.empleado,
          tipo_incidencia: '14', // Días Aguinaldo
          clase: 'AGUINALDO',
          dias: diasNumber,
          horas: 0,
          referencia: `Aguinaldo ${anio}`,
        };

        const res = await createMovimientoDiasHoras(payload);
        if (!res || !res.ok) {
          errores = 1;
          
        }
      }
      if (errores > 0){
         message.error(`Error al generar movimiento para los empeados`);
      }
      
      if (errores === 0) {
        message.success('Movimientos de días/horas generados correctamente.');
        await refreshButtonStates();
      }
    } catch (err) {
      message.error('Error al aplicar el aguinaldo.');
    } finally {
      setLoading(false);
    }
  };


  const handleCancelar = async () => {
    try {
      const values = await form.getFieldsValue(['anio', 'num_periodo', 'periodo_id']);
      
      if (!period || !period.periodoCompleto) {
        message.warning('Selecciona un período válido.');
        return;
      }

      const { periodo_id, num_periodo, estatus } = period.periodoCompleto;
      const anio = values.anio;

      if (estatus > status_period.calculado_total) {
        message.warning('El período seleccionado ya está cerrado o timbrado. No se puede aplicar el aguinaldo.');
        return;
      }

      if (!anio || !periodo_id || !num_periodo) {
        message.warning('Faltan datos del período.');
        return;
      }

      // Filtrar empleados con movimientos creados (los que tienen aguinaldo_calculado)
      const empleadosConMovimientos = rowsConAguinaldo.filter(
        (row) => row.aguinaldo_calculado !== undefined && row.aguinaldo_calculado !== null
      );

      if (!empleadosConMovimientos.length) {
        message.warning('No hay movimientos para cancelar.');
        return;
      }

      setLoading(true);

      let errores = 0;
      for (const row of empleadosConMovimientos) {
        const payload = {
          periodo_id,
          anio,
          num_periodo,
          odoo_empleado_id: row.empleado,
        };

        const res = await CancelarMovimientoDiasHoras(payload);
        if (!res || !res.ok) {
          errores += 1;
          message.error(
            res?.message || `Error al cancelar movimiento para ${row.nombre || row.empleado}`
          );
        }
      }

      if (errores === 0) {
        message.success('Movimientos cancelados correctamente.');
        // Limpiar tablas después de cancelar
        const current = form.getFieldsValue(['anio', 'num_periodo', 'periodo_id']);
        form.resetFields();
        form.setFieldsValue({
          ...current,
          anio_aguinaldo: anioActual,
        });
        setRowsConAguinaldo([]);
        setRowsSinAguinaldo([]);
        await refreshButtonStates();
      }
    } catch (err) {
      message.error('Error al cancelar los movimientos.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (period?.periodoCompleto?.estatus !== undefined) {
      setEstatusPeriodo(period.periodoCompleto.estatus);
    }
  }, [period]);


  return (
    <PageApp routes={routes} title="Aplicación de Aguinaldo">
      <Form
        layout="vertical"
        form={form}
        initialValues={{
          anio_aguinaldo: anioActual,
        }}
      >
        {/* Encabezado Año Nómina / Num Periodo / Tipo de Nómina */}
        <AnioPeriodo
          onBlur={onBlurHeader}
          form={form}
          disabledForm={objectSelected && objectSelected.id > 0}
        />

        <Divider orientation="left" orientationMargin="0">
          Aplicación de Aguinaldo
        </Divider>

        <Row gutter={16}>
          <Col md={{ span: 6 }} sm={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item
              name="anio_aguinaldo"
              label="Año Aguinaldo"
              rules={[{ required: true, message: 'El año de aguinaldo es requerido' }]}
            >
              <Input disabled={disabledForm} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col>
            <Button
              type="primary"
              onClick={handleMostrar}
              disabled={disabledForm || !period || estatusPeriodo === null || estatusPeriodo > status_period.calculado_total}
            >
              Mostrar
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={handleAplicar}
              loading={loading}
              disabled={disabledForm || !period || estatusPeriodo === null || estatusPeriodo > status_period.calculado_total || todosAplicados}
            >
              {loading ? 'Procesando' : 'Aplicar'}
            </Button>
          </Col>
          <Col>
            <Button onClick={handleCancelar} disabled={disabledForm || estatusPeriodo === null || estatusPeriodo > status_period.calculado_total || ningunMovimiento}>
              Desafectar
            </Button>
          </Col>
        </Row>

        <Divider />

        {loading ? (
          <Skeleton active />
        ) : (
          header &&
          !disabledForm && (
            <Tabs defaultActiveKey="con">
              <TabPane tab="Con Aguinaldo" key="con">
                <Card title="Empleados con cálculo de aguinaldo" size="small">
                  <Table
                    dataSource={rowsConAguinaldo}
                    columns={columnsConAguinaldo}
                    rowKey="key"
                    bordered
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText:
                        'No hay registros para el año de aguinaldo seleccionado.',
                    }}
                  />
                </Card>
              </TabPane>
              <TabPane tab="Sin Aguinaldo" key="sin">
                <Card title="Empleados sin aguinaldo aplicado" size="small">
                  <Table
                    dataSource={rowsSinAguinaldo}
                    columns={columnsSinAguinaldo}
                    rowKey="key"
                    bordered
                    pagination={false}
                    size="small"
                    locale={{
                      emptyText:
                        'No hay empleados pendientes de aplicar aguinaldo para este año.',
                    }}
                  />
                </Card>
              </TabPane>
            </Tabs>
          )
        )}
      </Form>
    </PageApp>
  );
};

export default AplicacionAguinaldo;
